import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import MetricsCard from './components/MetricsCard';
import FilterBar from './components/FilterBar';
import UsersTable from './components/UsersTable';
import ListingsTable from './components/ListingsTable';
import AnalysesTable from './components/AnalysesTable';
import { adminService, analysesService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('month');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const { user, userProfile, signOut } = useAuth();
  const [stats, setStats] = useState({ users: 0, listings: 0, analyses: 0, leads: 0 });
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Admin Dashboard - ListingLens';
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    const [statsData, usersData, listingsData, analysesData] = await Promise.all([
      adminService?.getStats(),
      adminService?.getAllUsers(),
      adminService?.getAllListings(),
      analysesService?.getAll()
    ]);
    setStats(statsData);
    setUsers(usersData?.data || []);
    setListings(listingsData?.data || []);
    setAnalyses(analysesData?.data || []);
    setLoading(false);
  };

  const metrics = [
    {
      title: 'Total Analyses',
      value: stats?.analyses?.toLocaleString() || '0',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'FileText',
      trend: [45, 52, 48, 61, 58, 67, 72]
    },
    {
      title: 'Active Users',
      value: stats?.users?.toLocaleString() || '0',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Users',
      trend: [32, 38, 35, 42, 45, 48, 52]
    },
    {
      title: 'Total Listings',
      value: stats?.listings?.toLocaleString() || '0',
      change: '+3.1%',
      changeType: 'positive',
      icon: 'Home',
      trend: [4, 5, 6, 7, 8, 9, 10]
    },
    {
      title: 'Total Leads',
      value: stats?.leads?.toLocaleString() || '0',
      change: '+5.4%',
      changeType: 'positive',
      icon: 'TrendingUp',
      trend: [3, 5, 4, 7, 6, 8, 9]
    }
  ];

  // Normalize users for table
  const normalizedUsers = users?.map(u => ({
    id: u?.id,
    name: u?.full_name || u?.email?.split('@')?.[0] || 'Unknown',
    email: u?.email || '',
    registrationDate: u?.created_at ? new Date(u.created_at)?.toLocaleDateString('en-AU') : '',
    analysisCount: 0,
    lastActive: u?.updated_at ? new Date(u.updated_at)?.toLocaleDateString('en-AU') : '',
    status: u?.is_active ? 'active' : 'inactive',
    role: u?.role || 'buyer'
  }));

  // Normalize listings for table
  const normalizedListings = listings?.map(l => ({
    id: l?.id,
    image: l?.media?.[0]?.original_url || null,
    imageAlt: `Property listing at ${l?.properties?.address || 'unknown address'}`,
    address: l?.properties?.address || 'Unknown Address',
    suburb: l?.properties ? `${l?.properties?.suburb}, ${l?.properties?.state}` : '',
    portal: 'listinglens',
    analysisCount: 0,
    completionRate: l?.status === 'live' ? 100 : 50,
    errorCount: 0,
    lastAnalyzed: l?.updated_at ? new Date(l.updated_at)?.toLocaleDateString('en-AU') : '',
    status: l?.status
  }));

  // Normalize analyses for table
  const normalizedAnalyses = analyses?.map(a => ({
    id: a?.id,
    property: a?.address || 'Unknown Property',
    suburb: a?.suburb || '',
    user: a?.user?.full_name || a?.user?.email || 'Unknown User',
    status: 'complete',
    verdict: a?.verdict || 'Fair',
    confidence: a?.confidence_score || 80,
    processingTime: '2.0s',
    createdAt: a?.created_at ? new Date(a.created_at)?.toLocaleString('en-AU') : ''
  }));

  const handleLogout = async () => {
    await signOut();
    navigate('/authentication');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = () => console.log('Exporting data...');
  const handleRefresh = () => loadAdminData();

  const tabs = [
    { id: 'users', label: 'Users', icon: 'Users', count: normalizedUsers?.length },
    { id: 'listings', label: 'Listings', icon: 'Home', count: normalizedListings?.length },
    { id: 'analyses', label: 'Analyses', icon: 'FileText', count: normalizedAnalyses?.length }
  ];

  const headerUser = user ? {
    id: user?.id,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'Admin',
    email: user?.email,
    role: 'admin'
  } : { id: 1, name: 'Admin User', email: 'admin@listinglens.com', role: 'admin' };

  return (
    <div className="min-h-screen bg-background">
      <Header user={headerUser} onLogout={handleLogout} />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Icon name="Shield" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                Admin Dashboard
              </h1>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Monitor platform usage, user activity, and system performance
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                {metrics?.map((metric, index) => (
                  <MetricsCard key={index} {...metric} />
                ))}
              </div>

              <FilterBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                onExport={handleExport}
                onRefresh={handleRefresh}
              />

              <div className="bg-card rounded-lg border border-border shadow-elevation-sm mb-4 md:mb-6 overflow-hidden">
                <div className="flex overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium transition-base flex-shrink-0 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} color="currentColor" />
                      <span>{tab?.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        activeTab === tab?.id
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {tab?.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="transition-base">
                {activeTab === 'users' && (
                  <UsersTable
                    users={normalizedUsers}
                    onSort={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                )}
                {activeTab === 'listings' && (
                  <ListingsTable
                    listings={normalizedListings}
                    onSort={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                )}
                {activeTab === 'analyses' && (
                  <AnalysesTable
                    analyses={normalizedAnalyses}
                    onSort={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;